import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMalak } from "@/providers/MalakProvider";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * v3.1 Proactive Intelligence Engine
 * Smart triggers based on page context, user history, and behavioral signals.
 * 
 * MOBILE: Never auto-opens fullscreen chat. Instead stores a proactive hint
 *   that shows a notification dot on the FAB. User taps → hint loads.
 * DESKTOP: Opens chat after idle period (unchanged behavior).
 * ANTI-SPAM: Max 1 proactive trigger per session, per-page dedup.
 */

interface ProactiveTrigger {
  pathMatch: RegExp;
  delayMs: number;
  messageKey: string;
  suggestionKeys: string[];
  /** Smart condition: only trigger if this returns true */
  condition?: () => boolean;
}

const TRIGGERS: ProactiveTrigger[] = [
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?(results)/,
    delayMs: 15000,
    messageKey: "malakChat.proactive.results",
    suggestionKeys: [
      "malakChat.proactive.comparePrices",
      "malakChat.proactive.cheapestOption",
      "malakChat.proactive.howBookingWorks",
    ],
  },
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?(quote)$/,
    delayMs: 20000,
    messageKey: "malakChat.proactive.quote",
    suggestionKeys: [
      "malakChat.proactive.cantFindItem",
      "malakChat.proactive.howPricingWorks",
      "malakChat.proactive.sendPersonal",
    ],
  },
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?(for-couriers)$/,
    delayMs: 12000,
    messageKey: "malakChat.proactive.forCouriers",
    suggestionKeys: [
      "malakChat.proactive.howSignUp",
      "malakChat.proactive.requirements",
      "malakChat.proactive.howMuchEarn",
    ],
  },
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?$/,
    delayMs: 30000,
    messageKey: "malakChat.proactive.homepage",
    suggestionKeys: [
      "malakChat.proactive.getQuote",
      "malakChat.proactive.howItWorks",
      "malakChat.proactive.trackShipment",
    ],
    condition: () => {
      // If user has an abandoned quote (started but didn't complete), nudge them
      try {
        const progress = localStorage.getItem('quoteProgress');
        if (!progress) return true;
        const data = JSON.parse(progress);
        if (data.pickupCountry && data.deliveryCountry) {
          // They have an incomplete quote — use the abandoned quote trigger instead
          return false;
        }
      } catch {}
      return true;
    },
  },
  // Abandoned quote recovery: triggers on homepage/non-quote pages when user has saved progress
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?$/,
    delayMs: 8000,
    messageKey: "malakChat.proactive.abandonedQuote",
    suggestionKeys: [
      "malakChat.proactive.continueQuote",
      "malakChat.proactive.startNewQuote",
      "malakChat.proactive.howPricingWorks",
    ],
    condition: () => {
      try {
        const progress = localStorage.getItem('quoteProgress');
        if (!progress) return false;
        const data = JSON.parse(progress);
        // Only trigger if they had a meaningful quote started (route selected)
        return !!(data.pickupCountry && data.deliveryCountry);
      } catch { return false; }
    },
  },
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?(how-it-works|about|faq|support|shipping)$/,
    delayMs: 12000,
    messageKey: "malakChat.proactive.abandonedQuote",
    suggestionKeys: [
      "malakChat.proactive.continueQuote",
      "malakChat.proactive.startNewQuote",
    ],
    condition: () => {
      try {
        const progress = localStorage.getItem('quoteProgress');
        if (!progress) return false;
        const data = JSON.parse(progress);
        return !!(data.pickupCountry && data.deliveryCountry);
      } catch { return false; }
    },
  },
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?(booking)/,
    delayMs: 25000,
    messageKey: "malakChat.proactive.booking",
    suggestionKeys: [
      "malakChat.proactive.howBookingWorks",
      "malakChat.proactive.cantFindItem",
    ],
  },
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?(custom-request)$/,
    delayMs: 15000,
    messageKey: "malakChat.proactive.customRequest",
    suggestionKeys: [
      "malakChat.proactive.howItWorks",
      "malakChat.proactive.getQuote",
    ],
  },
  {
    pathMatch: /^\/courier\/onboarding/,
    delayMs: 8000,
    messageKey: "malakChat.proactive.courierOnboarding",
    suggestionKeys: [
      "malakChat.proactive.whatDoINeed",
      "malakChat.proactive.howLongVerification",
      "malakChat.proactive.howMuchEarn",
    ],
  },
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?(faq)$/,
    delayMs: 10000,
    messageKey: "malakChat.proactive.faq",
    suggestionKeys: [
      "malakChat.proactive.getQuote",
      "malakChat.proactive.howItWorks",
      "malakChat.proactive.trackShipment",
    ],
    condition: () => {
      // Don't trigger if abandoned quote trigger would fire
      try {
        const progress = localStorage.getItem('quoteProgress');
        if (progress) {
          const data = JSON.parse(progress);
          if (data.pickupCountry && data.deliveryCountry) return false;
        }
      } catch {}
      return true;
    },
  },
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?(shipping)\/?$/,
    delayMs: 12000,
    messageKey: "malakChat.proactive.shippingRoutes",
    suggestionKeys: [
      "malakChat.proactive.getQuote",
      "malakChat.proactive.cheapestOption",
    ],
    condition: () => {
      try {
        const progress = localStorage.getItem('quoteProgress');
        if (progress) {
          const data = JSON.parse(progress);
          if (data.pickupCountry && data.deliveryCountry) return false;
        }
      } catch {}
      return true;
    },
  },
  {
    pathMatch: /^\/(en|fr|ar|es|it|se)?\/?(deposit-payment)/,
    delayMs: 20000,
    messageKey: "malakChat.proactive.depositPayment",
    suggestionKeys: [
      "malakChat.proactive.howBookingWorks",
    ],
  },
  {
    pathMatch: /^\/account\/bookings/,
    delayMs: 20000,
    messageKey: "malakChat.proactive.myBookings",
    suggestionKeys: [
      "malakChat.proactive.trackShipment",
    ],
    condition: () => !localStorage.getItem("malak_proactive_account"),
  },
  {
    pathMatch: /^\/courier\/?$/,
    delayMs: 15000,
    messageKey: "malakChat.proactive.courierDashboard",
    suggestionKeys: [
      "malakChat.proactive.pendingOrders",
      "malakChat.proactive.createTrip",
    ],
    condition: () => !localStorage.getItem("malak_proactive_courier_dash"),
  },
];

const TRIGGERED_KEY = "malak_proactive_triggered";
const TRIGGERED_PAGES_KEY = "malak_proactive_pages";

const IDLE_THRESHOLD_MS = 30000; // 30 seconds of zero activity
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "input"] as const;

export function useProactiveMalak() {
  const location = useLocation();
  const { t } = useTranslation();
  const { isOpen, messages, triggerProactive, setProactiveHint } = useMalak();
  const isMobile = useIsMobile();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOpenRef = useRef(isOpen);
  const messagesLenRef = useRef(messages.length);

  isOpenRef.current = isOpen;
  messagesLenRef.current = messages.length;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Don't trigger if chat is already open or has messages
    if (isOpen || messages.length > 0) return;

    // One trigger per session
    const triggered = localStorage.getItem(TRIGGERED_KEY);
    if (triggered) return;

    const triggeredPages: string[] = JSON.parse(localStorage.getItem(TRIGGERED_PAGES_KEY) || "[]");

    const trigger = TRIGGERS.find((tr) => {
      if (!tr.pathMatch.test(location.pathname)) return false;
      if (tr.condition && !tr.condition()) return false;
      return true;
    });

    if (!trigger) return;

    const pageKey = location.pathname.replace(/^\/(en|fr|ar|es|it|se)\//, "/");
    if (triggeredPages.includes(pageKey)) return;

    const startIdleTimer = () => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        if (isOpenRef.current || messagesLenRef.current > 0) return;
        if (localStorage.getItem(TRIGGERED_KEY)) return;

        const message = t(trigger.messageKey);
        const suggestions = trigger.suggestionKeys.map((k) => t(k));

        if (isMobile) {
          // MOBILE: Don't auto-open fullscreen chat — just set a hint dot
          setProactiveHint({ message, suggestedReplies: suggestions });
        } else {
          // DESKTOP: Open the chat panel with proactive message
          triggerProactive(message, suggestions);
        }

        triggeredPages.push(pageKey);
        localStorage.setItem(TRIGGERED_PAGES_KEY, JSON.stringify(triggeredPages));
        localStorage.setItem(TRIGGERED_KEY, "1");
      }, Math.max(trigger.delayMs, IDLE_THRESHOLD_MS));
    };

    const onActivity = () => startIdleTimer();

    startIdleTimer();

    ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, onActivity));
    };
  }, [location.pathname, isOpen, messages.length, triggerProactive, setProactiveHint, t, isMobile, clearTimer]);
}
